import { Head, router, usePage } from '@inertiajs/react'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import {
  HiAdjustmentsHorizontal,
  HiCheck,
  HiChevronLeft,
  HiChevronRight,
  HiMagnifyingGlass,
  HiOutlineBriefcase,
  HiOutlineCalendarDays,
  HiOutlineClock,
  HiOutlineEye,
  HiOutlineTrash,
  HiOutlineUserMinus,
} from 'react-icons/hi2'
import Avatar from '../../components/Avatar'
import ConfirmDialog from '../../components/ConfirmDialog'
import EventLayout from '../../components/EventLayout'
import FilterDrawer, {
  FilterFacet,
  FiltersState,
  filtersActiveCount,
} from '../../components/FilterDrawer'
import PriorityBadge from '../../components/PriorityBadge'
import SortByMenu, { SortKey } from '../../components/SortByMenu'

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

type EventStatus = 'upcoming' | 'ongoing' | 'completed'

type LeadPriority = 'hot' | 'warm' | 'cold'

type LeadStage = 'prospect' | 'in_progress' | 'proposal_sent'

type Lead = {
  id: number
  company: string
  name: string
  email: string
  priority: LeadPriority
  stage: LeadStage
  won: boolean
  assignee: { id: number; name: string } | null
  created_at_display: string
  updated_at_display: string
}

type Pagination = {
  current_page: number
  per_page: number
  total_count: number
  total_pages: number
  showing_from: number
  showing_to: number
}

type AssignableUser = {
  id: number
  name: string
  role: 'manager' | 'sales'
}

type Props = {
  event: {
    id: number
    title: string
    slug: string
    location: string
    date_range: string
    status: EventStatus
    banner_url: string | null
  }
  events: Array<{ title: string; slug: string }>
  active_tab: 'leads'
  leads: Lead[]
  pagination: Pagination
  q: string
  sort: SortKey
  filters: FiltersState
  filter_facets: FilterFacet[]
  assignable_users: AssignableUser[]
}

// ────────────────────────────────────────────────────────────────
// Display labels
// ────────────────────────────────────────────────────────────────

const STAGE_LABELS: Record<LeadStage, string> = {
  prospect: 'Prospect',
  in_progress: 'In Progress',
  proposal_sent: 'Proposal Sent',
}

const STAGES: LeadStage[] = ['prospect', 'in_progress', 'proposal_sent']

// ────────────────────────────────────────────────────────────────
// Page
// ────────────────────────────────────────────────────────────────

export default function Leads({
  event,
  events,
  leads,
  pagination,
  q,
  sort,
  filters,
  filter_facets,
  assignable_users,
}: Props) {
  const { props: shared } = usePage<{ current_user: { role: string } | null }>()
  const isManager = shared.current_user?.role === 'manager'
  const [pendingDelete, setPendingDelete] = useState<Lead | null>(null)
  const [filterOpen, setFilterOpen] = useState(false)

  // Unified navigation helper. Pass only the fields you want to change;
  // everything else comes from the current server-provided props so the
  // URL stays consistent.
  const navigate = (patch: {
    q?: string
    page?: number
    sort?: SortKey
    filters?: FiltersState
  }) => {
    const nextQ = patch.q ?? q
    const nextPage = patch.page ?? 1
    const nextSort = patch.sort ?? sort
    const nextFilters = patch.filters ?? filters

    router.get(
      `/events/${event.slug}/leads`,
      {
        q: nextQ || undefined,
        page: nextPage > 1 ? nextPage : undefined,
        sort: nextSort === 'default' ? undefined : nextSort,
        priority: nextFilters.priority.length > 0 ? nextFilters.priority : undefined,
        answers:
          Object.keys(nextFilters.answers).length > 0 ? nextFilters.answers : undefined,
        assignee_ids:
          nextFilters.assignee_ids.length > 0 ? nextFilters.assignee_ids : undefined,
      },
      {
        preserveState: true,
        preserveScroll: true,
        replace: true,
        only: ['leads', 'pagination', 'q', 'sort', 'filters'],
      },
    )
  }

  const handleDeleteConfirm = () => {
    if (!pendingDelete) return
    router.delete(`/leads/${pendingDelete.id}`, {
      preserveScroll: true,
      onFinish: () => setPendingDelete(null),
    })
  }

  const updateLead = (lead: Lead, patch: Partial<Pick<Lead, 'stage' | 'won'>>) => {
    router.patch(`/leads/${lead.id}`, patch, {
      preserveScroll: true,
      preserveState: true,
    })
  }

  const reassignLead = (lead: Lead, userId: number | null) => {
    router.patch(
      `/leads/${lead.id}`,
      { user_id: userId === null ? '' : userId },
      { preserveScroll: true, preserveState: true },
    )
  }

  return (
    <>
      <Head title={`${event.title} · Leads`} />
      <EventLayout event={event} events={events} active="leads">
        <TableToolbar
          q={q}
          sort={sort}
          activeFilterCount={filtersActiveCount(filters)}
          onSearch={(nextQ) => navigate({ q: nextQ, page: 1 })}
          onSortChange={(nextSort) => navigate({ sort: nextSort, page: 1 })}
          onFilterClick={() => setFilterOpen(true)}
        />

        {/* Mobile card view */}
        <div className="mt-4 space-y-3 md:hidden">
          {leads.length === 0 ? (
            <div className="overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
              <EmptyState query={q} />
            </div>
          ) : (
            leads.map((lead) => (
              <LeadCard
                key={lead.id}
                lead={lead}
                isManager={isManager}
                assignableUsers={assignable_users}
                onStageChange={(next) => updateLead(lead, { stage: next })}
                onWonChange={(next) => updateLead(lead, { won: next })}
                onReassign={(userId) => reassignLead(lead, userId)}
                onDelete={() => setPendingDelete(lead)}
              />
            ))
          )}
          <div className="overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)]">
            <PaginationFooter
              pagination={pagination}
              onPageChange={(next) => navigate({ page: next })}
            />
          </div>
        </div>

        {/* Desktop table view */}
        <div className="mt-4 hidden overflow-hidden rounded-xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.1),0_1px_2px_0_rgba(0,0,0,0.06)] md:block">
          {leads.length === 0 ? (
            <EmptyState query={q} />
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full border-separate border-spacing-0">
                <thead>
                  <tr className="bg-midnight-green">
                    <Th className="w-10">
                      <input
                        type="checkbox"
                        disabled
                        className="h-4 w-4 rounded border-white/40 bg-white/10"
                      />
                    </Th>
                    <Th>Company</Th>
                    <Th>Name</Th>
                    <Th>Priority</Th>
                    <Th>Stages</Th>
                    <Th>Assignee</Th>
                    <Th>Created</Th>
                    <Th>Updated</Th>
                    <Th className="text-center">Won</Th>
                    <Th className="text-center">Actions</Th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((lead) => (
                    <tr key={lead.id}>
                      <Td>
                        <input
                          type="checkbox"
                          disabled
                          className="h-4 w-4 rounded border-gray-300"
                        />
                      </Td>
                      <Td>
                        <span className="font-display text-sm font-medium text-ink">
                          {lead.company}
                        </span>
                      </Td>
                      <Td>
                        <div className="flex flex-col">
                          <span className="font-display text-sm text-ink">{lead.name}</span>
                          <span className="font-display text-xs text-black/40">
                            {lead.email}
                          </span>
                        </div>
                      </Td>
                      <Td>
                        <PriorityBadge priority={lead.priority} />
                      </Td>
                      <Td className="!px-2">
                        <StageSelect
                          value={lead.stage}
                          onChange={(next) => updateLead(lead, { stage: next })}
                        />
                      </Td>
                      <Td>
                        {isManager ? (
                          <AssigneeMenu
                            assignee={lead.assignee}
                            users={assignable_users}
                            onChange={(userId) => reassignLead(lead, userId)}
                          />
                        ) : lead.assignee ? (
                          <div className="flex items-center gap-2">
                            <Avatar id={lead.assignee.id} name={lead.assignee.name} />
                            <span className="font-display text-sm text-ink">
                              {lead.assignee.name}
                            </span>
                          </div>
                        ) : (
                          <span className="font-display text-sm italic text-black/40">
                            Unassigned
                          </span>
                        )}
                      </Td>
                      <Td>
                        <span className="font-display text-sm text-gray-600">
                          {lead.created_at_display}
                        </span>
                      </Td>
                      <Td>
                        <span className="font-display text-sm text-gray-600">
                          {lead.updated_at_display}
                        </span>
                      </Td>
                      <Td className="text-center">
                        <input
                          type="checkbox"
                          checked={lead.won}
                          onChange={(e) => updateLead(lead, { won: e.target.checked })}
                          className="h-5 w-5 rounded border-gray-300 text-[#93D500] focus:ring-[#93D500]"
                          aria-label={`Mark ${lead.name} as won`}
                        />
                      </Td>
                      <Td>
                        <div className="flex items-center justify-center gap-3">
                          <button
                            type="button"
                            onClick={() => router.visit(`/leads/${lead.id}`)}
                            className="text-gray-500 transition hover:text-midnight-green"
                            aria-label={`View ${lead.name}`}
                          >
                            <HiOutlineEye className="h-5 w-5" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setPendingDelete(lead)}
                            className="text-gray-500 transition hover:text-rose-600"
                            aria-label={`Delete ${lead.name}`}
                          >
                            <HiOutlineTrash className="h-5 w-5" />
                          </button>
                        </div>
                      </Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <PaginationFooter
            pagination={pagination}
            onPageChange={(next) => navigate({ page: next })}
          />
        </div>
      </EventLayout>

      <FilterDrawer
        open={filterOpen}
        onClose={() => setFilterOpen(false)}
        onApply={(next) => navigate({ filters: next, page: 1 })}
        value={filters}
        facets={filter_facets}
        assignees={isManager ? assignable_users : []}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        tone="danger"
        title="Delete this lead?"
        body={
          pendingDelete ? (
            <>
              You're about to delete <strong>{pendingDelete.name}</strong> from{' '}
              <strong>{pendingDelete.company}</strong>. This cannot be undone.
            </>
          ) : (
            ''
          )
        }
        confirmLabel="Delete"
        onConfirm={handleDeleteConfirm}
        onClose={() => setPendingDelete(null)}
      />
    </>
  )
}

// ────────────────────────────────────────────────────────────────
// Subcomponents — all inline, single caller per CLAUDE.md.
// Extract to components/ on second caller.
// ────────────────────────────────────────────────────────────────

function Th({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <th
      className={`border-r border-white/10 px-6 py-3 text-left font-display text-xs font-semibold uppercase tracking-wide text-white last:border-r-0 ${className}`}
    >
      {children}
    </th>
  )
}

function Td({ children, className = '' }: { children?: React.ReactNode; className?: string }) {
  return (
    <td
      className={`border-b border-r border-gray-200 bg-gray-50 px-6 py-3 last:border-r-0 ${className}`}
    >
      {children}
    </td>
  )
}

// ────────────────────────────────────────────────────────────────
// LeadCard — mobile view. Shows one lead per card with full parity
// of interactions (stage select, won toggle, delete).
// ────────────────────────────────────────────────────────────────

function LeadCard({
  lead,
  isManager,
  assignableUsers,
  onStageChange,
  onWonChange,
  onReassign,
  onDelete,
}: {
  lead: Lead
  isManager: boolean
  assignableUsers: AssignableUser[]
  onStageChange: (next: LeadStage) => void
  onWonChange: (next: boolean) => void
  onReassign: (userId: number | null) => void
  onDelete: () => void
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-1 items-start gap-3">
          <input
            type="checkbox"
            disabled
            className="mt-1 h-4 w-4 rounded border-gray-300"
          />
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-display text-base font-semibold text-ink">
              {lead.name}
            </h3>
            <p className="truncate font-display text-xs text-gray-500">{lead.email}</p>
          </div>
        </div>
        <Avatar id={lead.id} name={lead.name} size="md" />
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <PriorityBadge priority={lead.priority} />
        <StageSelect value={lead.stage} onChange={onStageChange} />
      </div>

      <div className="mt-4 space-y-2">
        <MetaRow icon={<HiOutlineBriefcase className="h-4 w-4" />} text={lead.company} />
        <MetaRow
          icon={<HiOutlineCalendarDays className="h-4 w-4" />}
          label="Created"
          text={lead.created_at_display}
        />
        <MetaRow
          icon={<HiOutlineClock className="h-4 w-4" />}
          label="Updated"
          text={lead.updated_at_display}
        />
        {isManager ? (
          <AssigneeMenu
            assignee={lead.assignee}
            users={assignableUsers}
            onChange={onReassign}
          />
        ) : lead.assignee ? (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <Avatar id={lead.assignee.id} name={lead.assignee.name} />
            <span className="font-display">{lead.assignee.name}</span>
          </div>
        ) : (
          <div className="text-sm italic text-black/40">Unassigned</div>
        )}
      </div>

      <div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-3">
        <label className="flex items-center gap-2 font-display text-sm text-gray-700">
          <input
            type="checkbox"
            checked={lead.won}
            onChange={(e) => onWonChange(e.target.checked)}
            className="h-5 w-5 rounded border-gray-300 text-[#93D500] focus:ring-[#93D500]"
          />
          Won
        </label>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => router.visit(`/leads/${lead.id}`)}
            className="text-gray-500 transition hover:text-midnight-green"
            aria-label={`View ${lead.name}`}
          >
            <HiOutlineEye className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="text-gray-500 transition hover:text-rose-600"
            aria-label={`Delete ${lead.name}`}
          >
            <HiOutlineTrash className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  )
}

function MetaRow({
  icon,
  text,
  label,
}: {
  icon: React.ReactNode
  text: string
  label?: string
}) {
  return (
    <div className="flex items-center gap-2 font-display text-sm text-gray-700">
      <span className="text-gray-500" title={label}>
        {icon}
      </span>
      {label && <span className="text-gray-500">{label}:</span>}
      <span className="truncate">{text}</span>
    </div>
  )
}

function TableToolbar({
  q,
  sort,
  activeFilterCount,
  onSearch,
  onSortChange,
  onFilterClick,
}: {
  q: string
  sort: SortKey
  activeFilterCount: number
  onSearch: (q: string) => void
  onSortChange: (sort: SortKey) => void
  onFilterClick: () => void
}) {
  const [value, setValue] = useState(q)
  const timer = useRef<number | null>(null)

  // Sync local state when server-provided q changes (e.g. switching events).
  useEffect(() => {
    setValue(q)
  }, [q])

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const next = e.target.value
    setValue(next)
    if (timer.current) window.clearTimeout(timer.current)
    timer.current = window.setTimeout(() => onSearch(next.trim()), 300)
  }

  return (
    <div className="flex items-center justify-between gap-2">
      <label className="flex min-w-0 flex-1 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 shadow-sm focus-within:ring-1 focus-within:ring-midnight-green md:max-w-sm md:flex-none md:w-80">
        <HiMagnifyingGlass className="h-4 w-4 text-gray-400" aria-hidden="true" />
        <input
          type="search"
          value={value}
          onChange={handleChange}
          placeholder="Search"
          className="w-full border-0 bg-transparent p-0 font-display text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-0"
        />
      </label>
      <div className="flex items-center gap-2">
        <SortByMenu sort={sort} onChange={onSortChange} />
        <button
          type="button"
          onClick={onFilterClick}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 font-display text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50"
        >
          <HiAdjustmentsHorizontal className="h-4 w-4" aria-hidden="true" />
          Filter
          {activeFilterCount > 0 && (
            <span className="ml-0.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-midnight-green px-1.5 font-display text-[10px] font-semibold text-white">
              {activeFilterCount}
            </span>
          )}
        </button>
      </div>
    </div>
  )
}

function StageSelect({
  value,
  onChange,
}: {
  value: LeadStage
  onChange: (next: LeadStage) => void
}) {
  // Figma-exact Prospect: CE Bright Green 20 fill (rgba(147,213,0,.2)) +
  // CE Bright Green #93D500 stroke. Other stages follow the same pattern
  // (20% opacity fill + solid border) in their hue.
  const styles: Record<LeadStage, { bg: string; border: string; text: string; caret: string }> = {
    prospect: {
      bg: 'rgba(147, 213, 0, 0.20)',
      border: '#93D500',
      text: '#3F5A00',
      caret: '%233F5A00',
    },
    in_progress: {
      bg: 'rgba(251, 191, 36, 0.25)',
      border: '#F59E0B',
      text: '#78350F',
      caret: '%2378350F',
    },
    proposal_sent: {
      bg: 'rgba(251, 146, 60, 0.25)',
      border: '#F97316',
      text: '#7C2D12',
      caret: '%237C2D12',
    },
  }
  const s = styles[value]
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as LeadStage)}
      className="appearance-none rounded-md border bg-[right_0.5rem_center] bg-no-repeat px-3 py-1.5 pr-8 font-display text-xs font-medium shadow-sm focus:outline-none focus:ring-1 focus:ring-midnight-green"
      style={{
        backgroundColor: s.bg,
        borderColor: s.border,
        color: s.text,
        backgroundImage: `url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20' stroke='${s.caret}' stroke-width='2'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
        backgroundSize: '14px',
      }}
    >
      {STAGES.map((stage) => (
        <option key={stage} value={stage}>
          {STAGE_LABELS[stage]}
        </option>
      ))}
    </select>
  )
}

function PaginationFooter({
  pagination,
  onPageChange,
}: {
  pagination: Pagination
  onPageChange: (page: number) => void
}) {
  const { current_page, total_pages, total_count, showing_from, showing_to } = pagination
  const pageNumbers = useMemo(() => buildPageRange(current_page, total_pages), [current_page, total_pages])

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3">
      <p className="font-display text-xs text-gray-500">
        Showing {showing_from} to {showing_to} of {total_count} results
      </p>

      {total_pages > 1 && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPageChange(current_page - 1)}
            disabled={current_page <= 1}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Previous page"
          >
            <HiChevronLeft className="h-4 w-4" />
          </button>
          {pageNumbers.map((p, i) =>
            p === '…' ? (
              <span key={`dots-${i}`} className="px-1 font-display text-sm text-gray-400">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                className={`flex h-8 min-w-8 items-center justify-center rounded-md border px-2 font-display text-sm font-medium transition ${
                  p === current_page
                    ? 'border-midnight-green bg-midnight-green text-white'
                    : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            ),
          )}
          <button
            type="button"
            onClick={() => onPageChange(current_page + 1)}
            disabled={current_page >= total_pages}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-gray-300 bg-white text-gray-600 transition hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-40"
            aria-label="Next page"
          >
            <HiChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  )
}

function EmptyState({ query }: { query: string }) {
  return (
    <div className="p-16 text-center">
      <p className="font-display text-base font-medium text-midnight-green">
        {query ? `No leads match "${query}"` : 'No leads yet'}
      </p>
      <p className="mt-1 font-display text-sm text-gray-500">
        {query
          ? 'Try a different search term.'
          : 'Leads assigned to you for this event will appear here.'}
      </p>
    </div>
  )
}

function AssigneeMenu({
  assignee,
  users,
  onChange,
}: {
  assignee: { id: number; name: string } | null
  users: AssignableUser[]
  onChange: (userId: number | null) => void
}) {
  const [open, setOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onClick = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false)
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onClick)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  const select = (userId: number | null) => {
    setOpen(false)
    if ((assignee?.id ?? null) === userId) return
    onChange(userId)
  }

  return (
    <div ref={wrapperRef} className="relative inline-block">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 rounded-md px-2 py-1 text-left transition hover:bg-gray-100"
      >
        {assignee ? (
          <>
            <Avatar id={assignee.id} name={assignee.name} />
            <span className="font-display text-sm text-ink">{assignee.name}</span>
          </>
        ) : (
          <>
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-gray-500">
              <HiOutlineUserMinus className="h-3.5 w-3.5" />
            </span>
            <span className="font-display text-sm italic text-black/40">Unassigned</span>
          </>
        )}
      </button>

      {open && (
        <div className="absolute left-0 z-20 mt-1 max-h-72 w-60 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg">
          <button
            type="button"
            onClick={() => select(null)}
            className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-gray-50"
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-200 text-gray-500">
              <HiOutlineUserMinus className="h-3.5 w-3.5" />
            </span>
            <span className="flex-1 font-display text-sm italic text-black/60">
              Unassigned
            </span>
            {assignee === null && (
              <HiCheck className="h-4 w-4 text-midnight-green" aria-hidden="true" />
            )}
          </button>
          <div className="my-1 border-t border-gray-100" />
          {users.map((u) => {
            const isCurrent = assignee?.id === u.id
            return (
              <button
                key={u.id}
                type="button"
                onClick={() => select(u.id)}
                className="flex w-full items-center gap-2 px-3 py-2 text-left transition hover:bg-gray-50"
              >
                <Avatar id={u.id} name={u.name} />
                <span className="flex-1 truncate font-display text-sm text-ink">
                  {u.name}
                </span>
                <span className="font-display text-[10px] uppercase tracking-wide text-black/40">
                  {u.role}
                </span>
                {isCurrent && (
                  <HiCheck className="h-4 w-4 text-midnight-green" aria-hidden="true" />
                )}
              </button>
            )
          })}
        </div>
      )}
    </div>
  )
}

// Build a compact page list like [1, 2, 3, '…', 10] for the pagination strip.
function buildPageRange(current: number, total: number): Array<number | '…'> {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)

  const pages: Array<number | '…'> = [1]
  const start = Math.max(2, current - 1)
  const end = Math.min(total - 1, current + 1)

  if (start > 2) pages.push('…')
  for (let p = start; p <= end; p++) pages.push(p)
  if (end < total - 1) pages.push('…')
  pages.push(total)
  return pages
}
